import React from 'react';
import { useSelector } from 'react-redux';
import { Item, Image, Segment } from 'semantic-ui-react';
import { StatusLabels } from '../statusTools/StatusIndicators';
import { getPriorityColor, getStatusColor } from '../../constants/statusConfig';
import characterImage from '../../assets/shutterstock_2294275225-480x480.jpg';
import useReduxSignedUrlV2 from '../../hooks/useReduxSignedUrlV2';

/**
 * Shared character item card component used by both Voice Request List and My Voice Portfolio
 * 
 * @param {Object} character - The character object with all its properties
 * @param {string} userId - The user ID for authentication
 * @param {Function} renderVariations - Function that renders the variations section
 * @param {React.ReactNode} headerExtras - Optional extra content for the header section
 * @returns {JSX.Element}
 */
const CharacterItemCard = ({
  character,
  userId,
  renderVariations,
  headerExtras
}) => {
  // Get signed URL for avatar using Redux cache with error handling
  const { signedUrl: signedAvatarUrl, handleImageError } = useReduxSignedUrlV2(character.avatarUrl, userId);

  // Map character data to UI format
  const header = character.name;
  const description = character.description || "No description available";
  const portfolioName = character.portfolioName || "Unknown Portfolio";
  const created_date = new Date(character.createdAt).toLocaleDateString();
  const updated_date = new Date(character.updatedAt).toLocaleDateString();

  // Use character's avatar if it exists, otherwise use default image
  const imgSrc = signedAvatarUrl || characterImage;

  // Get the actual priority, status, and deadline values
  const priority = character.priority || "Medium";
  const status = character.status || "New";
  const deadline = character.deadline || null;

  return (
    <Item>
      <Item.Content>
        {/* Header and Description Section with Image */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', backgroundColor: "#ececec", padding: "8px", borderRadius: "8px" }}>
          <Image
            size='tiny'
            src={imgSrc}
            onError={handleImageError}
          />
          <div style={{ marginLeft: '15px' }}>
            <Item.Header as='a' style={{ width: "100%", fontSize: "1.3rem" }}>
              {header}
            </Item.Header>
            <Item.Meta>{portfolioName}</Item.Meta>
            <Item.Description>
              <p style={{ maxWidth: "600px" }}>{description}</p>
              <StatusLabels
                priority={priority}
                status={status}
                deadline={deadline}
                getPriorityColor={getPriorityColor}
                getStatusColor={getStatusColor}
              />
            </Item.Description>
            <Item.Extra>
              created: <span>{created_date}</span>
              <span className="listUpdatedText">updated: {updated_date}</span>
            </Item.Extra>
            {headerExtras}
          </div>
        </div>

        {/* Variations Section */}
        <div style={{ padding: "0px !important" }}>
          <div className="fullHeaderFont" style={{ marginBottom: '10px' }}>
            {character.isRequest ? "Required Emotions:" : "Voice Variations:"}
          </div>
          {renderVariations()}
        </div>
      </Item.Content>
    </Item>
  );
};

export default CharacterItemCard;
